import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Select,
  Spacer,
  Table,
  Tbody,
  Td,
  Text as ChakraText,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import _ from 'lodash'
import moment from 'moment'
import numeral from 'numeral'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import type { CellProps, Column } from 'react-table'
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { BaseLayout } from '..'
import Pagination from '../../components/Pagination'
import SearchBar from '../../components/SearchBar'
import useToast from '../../hooks/useToast'
import type AgencyAnalyticsResponse from '../../interfaces/AgencyAnalyticsResponse'
import type { Usage } from '../../interfaces/AgencyAnalyticsResponse'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'

const AgencyAnalyticsLayout = (): React.ReactElement => {
  const { t } = useTranslation()

  const toast = useToast()

  const [selectedMonth, setSelectedMonth] = useState<{
    monthStart: string
    monthEnd: string
    label: string
  } | null>(null)

  const months = useMemo(() => {
    const currentMonthStart = moment().startOf('month')
    const currentMonthEnd = moment().endOf('month')

    const monthObj = [
      {
        monthStart: currentMonthStart.format('YYYY-MM-DD'),
        monthEnd: currentMonthEnd.format('YYYY-MM-DD'),
        label: currentMonthStart.format('MMMM YYYY'),
      },
    ]

    setSelectedMonth(monthObj[0])

    for (let i = 1; i <= 11; i++) {
      const monthStart = moment(currentMonthStart)
        .subtract(i, 'month')
        .startOf('month')
      const monthEnd = moment(currentMonthStart)
        .subtract(i, 'month')
        .endOf('month')
      monthObj.push({
        monthStart: monthStart.format('YYYY-MM-DD'),
        monthEnd: monthEnd.format('YYYY-MM-DD'),
        label: monthStart.format('MMMM YYYY'),
      })
    }

    return _.reverse(monthObj)
  }, [])

  const { user } = useContext(UserContext)

  const [data, setData] = useState<AgencyAnalyticsResponse[]>()

  const { request } = useFetch<AgencyAnalyticsResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getData = async (): Promise<void> => {
    if (user?.clientNumber) {
      let url = `/api/clients/${user.clientNumber}/agency/child/usage`
      if (selectedMonth) {
        const searchParams = new URLSearchParams({
          skip: '0',
          limit: '50',
          fromDate: selectedMonth.monthStart,
          toDate: selectedMonth.monthEnd,
        })

        url = `${url}?${searchParams.toString()}`
      }

      const response = await request.get(url)

      if (request.error) {
        toast({
          description: t('error_fetching_data', 'Error in fetching data. Try refreshing the page.'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setData(response)
      }
    }
  }

  const exportCSV = async (): Promise<void> => {
    if (user?.clientNumber) {
      let url = `/api/clients/${user.clientNumber}/agency/child/usage/export`
      if (selectedMonth) {
        const searchParams = new URLSearchParams({
          skip: '0',
          limit: '50',
          fromDate: selectedMonth.monthStart,
          toDate: selectedMonth.monthEnd,
        })

        url = `${url}?${searchParams.toString()}`
      }

      await request.get(url)

      if (request.error) {
        toast({
          description: t('something_went_wrong', 'Something went wrong!'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }

      toast({
        description: t(
          'agency_export_csv_mail_sent',
          'An email containing the report has been sent to your registered email id.',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }

  useEffect(() => {
    void getData()
  }, [user])

  const tableData = useMemo<AgencyAnalyticsResponse[]>(() => data ?? [], [data])

  const columns = useMemo<Array<Column<AgencyAnalyticsResponse>>>(
    () => [
      {
        Header: t('client_name', 'Client Name').toString(),
        accessor: ({ clientName }) => clientName,
      },
      {
        Header: t('imagekit_id', 'ImageKit ID').toString(),
        accessor: ({ imagekitId }) => imagekitId,
      },
      {
        Header: t('processing_region', 'Processing Region').toString(),
        accessor: ({ region }) => region,
      },
      {
        Header: t('bill_to', 'Bill To').toString(),
        accessor: ({ billTo }) => _.capitalize(billTo),
      },
      {
        Header: t('bandwidth_consumed', 'Bandwidth Consumed').toString(),
        accessor: ({ usage }) => {
          if (usage) {
            return usage;
          }
          return 0
        },
        Cell: ({
          value,
        }: {
          value: Usage
        }) => {
          if (value.bandwidth) {
            return numeral(value.bandwidth).format('0.00 ib').replace('i', '')
          }
          return '-'
        }
      },
      {
        Header: t('requests_made', 'Requests Made').toString(),
        accessor: ({ usage }) => {
          if (usage) {
            return numeral(usage.request).format('0.0 a')
          }
          return '-'
        },
      },
      {
        Header: t('created_on', 'Created On').toString(),
        accessor: ({ createdAt }) => new Date(createdAt),
        sortType: 'datetime',
        Cell: ({ value }: CellProps<AgencyAnalyticsResponse, Date>) =>
          moment(value).format('MMMM Do YYYY, h:mm:ss a'),
      },
    ],
    [],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    state: tableState,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    setGlobalFilter,
    setPageSize,
    previousPage,
    nextPage,
    gotoPage,
  } = useTable<AgencyAnalyticsResponse>(
    { columns, data: tableData },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  const pageOffset = tableState.pageIndex * tableState.pageSize

  return (
    <BaseLayout>
      <MainContentHeadingBox>
        <Heading
          as="h5"
          size="md"
          fontWeight="600"
          color={colors.appDarkIndigo}
          alignSelf="center"
        >
          {t('analytics', 'analytics')}
        </Heading>
      </MainContentHeadingBox>
      <MainContentWrapper style={{ gap: '0.5em' }}>
        <Flex justifyContent="space-between" marginY="3">
          <HStack>
            <Select
              value={JSON.stringify(selectedMonth)}
              onChange={e => {
                setSelectedMonth(JSON.parse(e.target.value))
              }}
            >
              {months.map(x => (
                <option key={x.monthStart} value={JSON.stringify(x)}>
                  {x.label}
                </option>
              ))}
            </Select>
            <ButtonGroup>
              <Button onClick={getData}>{t('view', 'View')}</Button>
              <Button variant="outline" onClick={exportCSV}>
                {t('export_csv', 'Export CSV')}
              </Button>
            </ButtonGroup>
          </HStack>
          <Spacer />
          <HStack>
            {rows.length ? (
              <ChakraText fontWeight="500">
                Showing {pageOffset + 1} to{' '}
                {pageOffset + tableState.pageSize <= rows.length
                  ? pageOffset + tableState.pageSize
                  : rows.length}{' '}
                of {rows.length} entries
              </ChakraText>
            ) : (
              <ChakraText fontWeight="500">No entries available</ChakraText>
            )}
            <SearchBar
              searchQuery={tableState.globalFilter as string}
              setSearchQuery={setGlobalFilter}
              minWidth="30em"
            />
          </HStack>
        </Flex>
        <Table {...getTableProps}>
          <Thead bgColor="appPaleGreyTwo">
            {headerGroups.map(headerGroup => {
              const { key, ...restHeaderGroupProps } =
                headerGroup.getHeaderGroupProps()
              return (
                <Tr key={key} {...restHeaderGroupProps}>
                  {headerGroup.headers.map(column => {
                    const { key: keyCol, ...restColumn } =
                      column.getHeaderProps(column.getSortByToggleProps())
                    return (
                      <Th key={keyCol} {...restColumn}>
                        {column.render('Header')}
                        {!column.disableSortBy ? (
                          <Box
                            as="span"
                            pl="2"
                            color={colors.appBattleshipGrey}
                          >
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <Icon
                                  as={FaSortUp}
                                  aria-label={t(
                                    'sorted_descending',
                                    'sorted descending',
                                  )}
                                />
                              ) : (
                                <Icon
                                  as={FaSortDown}
                                  aria-label={t(
                                    'sorted_ascending',
                                    'sorted ascending',
                                  )}
                                />
                              )
                            ) : (
                              <Icon
                                as={FaSort}
                                aria-label={t(
                                  'sort_by_column',
                                  'sort by column',
                                )}
                              />
                            )}
                          </Box>
                        ) : null}
                      </Th>
                    )
                  })}
                </Tr>
              )
            })}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {rows.length ? (
              page.map(row => {
                prepareRow(row)
                const { key, ...restRowProps } = row.getRowProps()
                return (
                  <Tr key={key} {...restRowProps}>
                    {row.cells.map(cell => {
                      const { key: keyRow, ...restCellProps } =
                        cell.getCellProps()
                      return (
                        <Td key={keyRow} {...restCellProps}>
                          <ChakraText>{cell.render('Cell')}</ChakraText>
                        </Td>
                      )
                    })}
                  </Tr>
                )
              })
            ) : (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  <ChakraText>No accounts available</ChakraText>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
        {pageOptions.length > 0 ? <Divider mt={4} mb={4} /> : null}
        <Flex justifyContent="flex-end" alignItems="center">
          <Pagination
            pageIndex={tableState.pageIndex}
            pageSize={tableState.pageSize}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            pageCount={pageCount}
            pageOptions={pageOptions}
            setPageSize={setPageSize}
            previousPage={previousPage}
            nextPage={nextPage}
            gotoPage={gotoPage}
          />
        </Flex>
      </MainContentWrapper>
    </BaseLayout>
  )
}

export default AgencyAnalyticsLayout
